/* global process */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaRegUser } from "react-icons/fa";
import { initializeApp } from "firebase/app";
import {
   getAuth,
   onAuthStateChanged,
   signInWithPopup,
   signInWithEmailAndPassword,
   GoogleAuthProvider,
   signOut,
   signInWithPhoneNumber,
   RecaptchaVerifier,
} from "firebase/auth";
import { FcGoogle } from "react-icons/fc"; // Google Icon
import { FaPhoneAlt } from "react-icons/fa"; // Phone Icon


// Load Firebase config from JSON file
import firebaseConfig from "../../firebaseConfig.json";


// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const provider = new GoogleAuthProvider();


const Welcome = () => {
   const navigate = useNavigate();
   const [variant, setVariant] = useState("default");
   const [loading, setLoading] = useState(true);
   const [user, setUser] = useState(null);
   const [email, setEmail] = useState("");
   const [password, setPassword] = useState("");
   const [phoneNumber, setPhoneNumber] = useState("");
   const [verificationCode, setVerificationCode] = useState("");
   const [confirm, setConfirm] = useState(null);


   useEffect(() => {
       const fetchVariant = async () => {
           try {
               const response = await axios.get(
                   `${process.env.REACT_APP_BACKEND_URL || "http://localhost:5002"}/api/get-welcome-variant`
               );
               setVariant(response.data.variant);
           } catch {
               setVariant("default");
           } finally {
               setLoading(false);
           }
       };


       fetchVariant();


       // Check Firebase Auth State
       onAuthStateChanged(auth, async (currentUser) => {
           setUser(currentUser);
           if (currentUser) {
               // Redirect to the same page as guest user after login
               try {
                   await axios.post(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:5002"}/api/welcome-button-click`, {
                       userId: currentUser.uid,
                       testName: "welcome_button",
                       eventType: variant,
                   });
               } catch (error) {
                   console.warn(error.message);
               }
               navigate("/url-list");
           }
       });
   }, [variant, navigate]);


   const goToUrlList = async () => {
       try {
           await axios.post(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:5002"}/api/welcome-button-click`, {
               userId: user ? user.uid : "anonymous",
               testName: "welcome_button",
               eventType: variant,
           });
       } catch (error) {
           console.warn(error.message);
       }
       navigate("/url-list");
   };


   const handleGoogleLogin = async () => {
       try {
           const result = await signInWithPopup(auth, provider);
           // Handle successful login
           try {
               await axios.post(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:5002"}/api/welcome-button-click`, {
                   userId: result.user.uid,
                   testName: "welcome_button",
                   eventType: variant,
               });
           } catch (error) {
               console.warn(error.message);
           }
           navigate("/url-list");
       } catch (error) {
           console.error("Google Sign-in Error:", error.message);
       }
   };


   const handleEmailLogin = async () => {
       try {
           await signInWithEmailAndPassword(auth, email, password);
       } catch (error) {
           console.error("Email Sign-in Error:", error.message);
       }
   };


   const handleLogout = async () => {
       try {
           await signOut(auth);
           setUser(null);
       } catch (error) {
           console.error("Logout Error:", error.message);
       }
   };


   const handleGuestLogin = async () => {
       // Logic for signing in as a guest (similar to Get Started button)
       try {
           await axios.post(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:5002"}/api/welcome-button-click`, {
               userId: "anonymous",
               testName: "welcome_button",
               eventType: variant,
           });
       } catch (error) {
           console.warn(error.message);
       }
       navigate("/url-list");
   };


   const handlePhoneLogin = async () => {
       const recaptchaVerifier = new RecaptchaVerifier(
           "recaptcha-container",
           {
               size: "invisible",
               callback: (response) => {
                   console.log("Recaptcha verified:", response);
               },
           },
           auth
       );


       try {
           const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
           setConfirm(confirmationResult);
       } catch (error) {
           console.error("Phone Sign-in Error:", error.message);
       }
   };


   const handleCodeVerification = async () => {
       if (confirm) {
           try {
               await confirm.confirm(verificationCode);
           } catch (error) {
               console.error("Verification Code Error:", error.message);
           }
       }
   };


   const renderButton = () => {
       if (loading) {
           return (
               <button className="px-6 py-3 text-lg font-medium text-white bg-gray-400 rounded-full">
                   Loading...
               </button>
           );
       }


       let buttonStyle = "";
       let buttonText = "Get Started!";


       switch (variant) {
           case "blue":
               buttonStyle = "px-6 py-3 text-lg font-medium text-white bg-blue-500 rounded-full hover:bg-blue-600";
               break;
           case "green":
               buttonStyle = "px-6 py-3 text-lg font-medium text-white bg-green-500 rounded-full hover:bg-green-600";
               break;
           case "red":
               buttonStyle = "px-6 py-3 text-lg font-medium text-white bg-red-500 rounded-full hover:bg-red-600";
               break;
           default:
               buttonStyle = "px-6 py-3 text-lg font-medium text-white bg-purple-500 rounded-full hover:bg-purple-600";
       }


       return (
           <button onClick={goToUrlList} className={buttonStyle}>
               {buttonText}
           </button>
       );
   };


   return (
       <div className="flex flex-col items-center justify-center min-h-screen" data-testid="main-container">
           <div className="flex flex-col items-center justify-center p-10 text-center transition-transform duration-300 ease-in-out transform bg-white shadow-xl bg-opacity-80 rounded-3xl hover:scale-105">
               <h1 className="mb-5 text-6xl font-bold text-purple-700">Welcome to Dwello!</h1>
               <p className="text-xl text-gray-700 mb-7">
                   Say hello to your new dwelling. Compare properties for your next adventure!
               </p>


               {/* If user is logged in */}
               {user ? (
                   <>
                       <p className="text-lg text-gray-800 mb-3">Welcome, {user.email}!</p>
                       <button
                           onClick={handleLogout}
                           className="mb-4 px-6 py-3 text-lg font-medium text-white bg-red-500 rounded-full hover:bg-red-600"
                       >
                           Logout
                       </button>
                   </>
               ) : (
                   <>
                       {/* Google Login Button */}
                       <button
                           onClick={handleGoogleLogin}
                           className="flex items-center justify-center mb-4 px-6 py-3 text-lg font-medium text-black bg-white border border-gray-300 rounded-full hover:bg-gray-100 w-72"
                       >
                           <FcGoogle className="text-2xl mr-3" />
                           Sign in with Google
                       </button>


                       {/* Phone Login Button */}
                       {/* <button
                           onClick={handlePhoneLogin}
                           className="flex items-center justify-center mb-4 px-6 py-3 text-lg font-medium text-black bg-white border border-gray-300 rounded-full hover:bg-gray-100 w-72"
                       >
                           <FaPhoneAlt className="text-2xl mr-3" />
                           Sign in with Phone
                       </button> */}


                       {/* Sign in as Guest Button */}
                       <button
                           onClick={handleGuestLogin}
                           className="flex items-center justify-center mb-4 px-6 py-3 text-lg font-medium text-black bg-white border border-gray-300 rounded-full hover:bg-gray-100 w-72"
                       >
                           <FaRegUser className="text-2xl mr-3" />
                           Sign in as Guest
                       </button>


                       {/* OR Divider with Line */}
                       {/* <div className="mb-4 text-lg text-gray-500">--- OR ---</div> */}


                       {/* Email Login Fields */}
                       {/* <input
                           type="email"
                           placeholder="Email"
                           value={email}
                           onChange={(e) => setEmail(e.target.value)}
                           className="mb-2 w-72 px-4 py-2 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                       />
                       <input
                           type="password"
                           placeholder="Password"
                           value={password}
                           onChange={(e) => setPassword(e.target.value)}
                           className="mb-4 w-72 px-4 py-2 text-lg border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                       /> */}


                       {/* Email Login Button */}
                       {/* <button
                           onClick={handleEmailLogin}
                           className="mb-4 px-6 py-3 text-lg font-medium text-white bg-purple-500 rounded-full hover:bg-purple-600 w-72"
                       >
                           Sign in with Email
                       </button> */}
                   </>
               )}
           </div>
       </div>
   );
};


export default Welcome;


